/**
 * Typed fetch wrapper for the SpotMesh API.
 *
 * All calls that touch tenant data send `X-Tenant-Id` (the placeholder
 * auth used until the Cognito JWT verifier ships). Bearer token is also
 * included if present so a JWT migration is backend-only.
 */
import type {
  PackageRow,
  RouterRow,
  CreateRouterResponse,
  VoucherRow,
  RouterConfigResponse,
  TenantRow,
} from "../lib/types.ts";

const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  "https://api.spotmesh.cloud";

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

type Auth = { tenantId: string; idToken?: string };

async function request<TResp>(
  path: string,
  init: RequestInit & Auth,
): Promise<TResp> {
  const { tenantId, idToken, headers, ...rest } = init;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(idToken
        ? { Authorization: `Bearer ${idToken}` }
        : { "X-Tenant-Id": tenantId }),
      ...headers,
    },
  });

  const text = await res.text();
  const body: unknown = text ? safeJson(text) : null;
  if (!res.ok) {
    const err: ApiError = Object.assign(
      new Error(
        `API ${res.status} ${res.statusText}: ${path}${
          body ? " — " + JSON.stringify(body).slice(0, 200) : ""
        }`,
      ),
      { status: res.status, body },
    );
    throw err;
  }
  return body as TResp;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ---- Public endpoints (no tenant header) ----

export async function getHealth(): Promise<{ ok: boolean; service: string }> {
  const res = await fetch(`${API_BASE_URL}/health`);
  if (!res.ok) throw new Error(`/health ${res.status}`);
  return res.json() as Promise<{ ok: boolean; service: string }>;
}

// ---- Packages ----

export const packagesApi = {
  list: (auth: Auth) => request<PackageRow[]>("/packages", { ...auth, method: "GET" }),
  create: (
    auth: Auth,
    body: {
      name: string;
      durationMinutes: number;
      priceKes: number;
      dataMbCap?: number;
      routerId?: string | null;
      sortOrder?: number;
    },
  ) =>
    request<PackageRow>("/packages", {
      ...auth,
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (
    auth: Auth,
    id: string,
    body: Partial<{
      name: string;
      durationMinutes: number;
      priceKes: number;
      dataMbCap: number | null;
      routerId: string | null;
      sortOrder: number;
      active: boolean;
    }>,
  ) =>
    request<PackageRow>(`/packages/${id}`, {
      ...auth,
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};

// ---- Routers ----

export const routersApi = {
  list: (auth: Auth) => request<RouterRow[]>("/routers", { ...auth, method: "GET" }),
  create: (auth: Auth, body: { name: string; hotspotSubdomain?: string }) =>
    request<CreateRouterResponse>("/routers", {
      ...auth,
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (
    auth: Auth,
    id: string,
    body: Partial<{
      name: string;
      hotspotSubdomain: string | null;
      status: "pending" | "online" | "offline" | "disabled";
    }>,
  ) =>
    request<RouterRow>(`/routers/${id}`, {
      ...auth,
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};

// ---- Vouchers ----

export const vouchersApi = {
  list: (
    auth: Auth,
    query: Partial<{
      status: VoucherRow["status"];
      routerId: string;
      from: string;
      to: string;
      limit: number;
    }> = {},
  ) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const q = qs.toString();
    return request<VoucherRow[]>(`/vouchers${q ? `?${q}` : ""}`, {
      ...auth,
      method: "GET",
    });
  },
};

// ---- Onboarding wizard ----

export const onboardingApi = {
  routerConfig: (
    auth: Auth,
    routerId: string,
    body: {
      routerOsVersion: "6" | "7";
      hotspotInterface: string;
      hotspotBridge?: string;
      wanInterface?: string;
      hotspotSubnet?: string;
    },
  ) =>
    request<RouterConfigResponse>(`/onboarding/routers/${routerId}/config`, {
      ...auth,
      method: "POST",
      body: JSON.stringify({ tenantId: auth.tenantId, ...body }),
    }),
};

// ---- Tenants (self-service provisioning + settings) ----

type IdAuth = { idToken: string };

async function jwtRequest<TResp>(
  path: string,
  init: RequestInit & IdAuth,
): Promise<TResp> {
  const { idToken, headers, ...rest } = init;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...headers,
    },
  });
  const text = await res.text();
  const body: unknown = text ? safeJson(text) : null;
  if (!res.ok) {
    const err: ApiError = Object.assign(
      new Error(
        `${init.method ?? "GET"} ${path} ${res.status}: ${
          body ? JSON.stringify(body).slice(0, 200) : res.statusText
        }`,
      ),
      { status: res.status, body },
    );
    throw err;
  }
  return body as TResp;
}

export const tenantsApi = {
  create: (idToken: string, body: { name: string; phone?: string }) =>
    jwtRequest<TenantRow>("/tenants", {
      idToken,
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateTenant: (
    idToken: string,
    body: Partial<{
      name: string;
      phone: string | null;
      billingModel: "flat" | "revenue_share";
    }>,
  ) =>
    jwtRequest<TenantRow>("/me/tenant", {
      idToken,
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  getDarajaStatus: (idToken: string) =>
    jwtRequest<{ configured: boolean }>("/me/tenant/daraja", {
      idToken,
      method: "GET",
    }),

  putDaraja: (
    idToken: string,
    body: {
      environment: "sandbox" | "production";
      shortcode: string;
      consumerKey: string;
      consumerSecret: string;
      passkey: string;
      transactionType: "CustomerPayBillOnline" | "CustomerBuyGoodsOnline";
    },
  ) =>
    jwtRequest<{ ok: boolean; configured: boolean }>("/me/tenant/daraja", {
      idToken,
      method: "PUT",
      body: JSON.stringify(body),
    }),
};

export { API_BASE_URL };
